import React, { useState, useEffect } from 'react';
import { membershipTierAPI, planAPI } from '../../services/api';
import { useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import './MembershipTiers.css';

const DEFAULT_FORM = {
    name: '', level: 1, planId: '', color: '#94a3b8', icon: 'fa-medal',
    criteria: { minStays: 0, minSpend: 0 }, isActive: true,
};

const MembershipTiers = () => {
    const { selectedBranch } = useBranchStore();
    const [tiers, setTiers] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formData, setFormData] = useState(DEFAULT_FORM);

    useEffect(() => { fetchAll(); }, [selectedBranch]);

    const fetchAll = async () => {
        try {
            setLoading(true);
            const branchId = selectedBranch ? selectedBranch._id : undefined;
            const [tierRes, planRes] = await Promise.all([
                membershipTierAPI.getAll(branchId ? { branchId } : undefined),
                planAPI.getAll(branchId),
            ]);
            setTiers(tierRes.data.data || []);
            setPlans(planRes.data.data.plans || []);
        } catch (error) {
            toast.error('Failed to fetch membership tiers');
        } finally {
            setLoading(false);
        }
    };

    const handleNew = () => { setEditing(null); setFormData(DEFAULT_FORM); setShowModal(true); };

    const handleEdit = (tier) => {
        setEditing(tier);
        setFormData({
            name: tier.name, level: tier.level, planId: tier.planId?._id || tier.planId || '',
            color: tier.color, icon: tier.icon,
            criteria: { minStays: tier.criteria?.minStays || 0, minSpend: tier.criteria?.minSpend || 0 },
            isActive: tier.isActive !== false,
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) return toast.warning('Name is required');
        try {
            if (editing) {
                await membershipTierAPI.update(editing._id, formData);
                toast.success('Tier updated');
            } else {
                await membershipTierAPI.create(formData);
                toast.success('Tier created');
            }
            setShowModal(false);
            fetchAll();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const handleDelete = async (tier) => {
        if (!window.confirm(`Delete tier "${tier.name}"?`)) return;
        try {
            await membershipTierAPI.delete(tier._id);
            toast.success('Tier deleted');
            fetchAll();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern"><i className="fas fa-medal"></i> Membership Tiers</h1>
                        <p className="page-subtitle-modern">Silver/Gold/Platinum/Diamond loyalty tiers — auto-upgraded based on guest stay count or spend, applied at checkout</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-primary-modern" onClick={handleNew}><i className="fas fa-plus-circle"></i> Add Tier</button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-modern"><div className="spinner-modern"></div><p>Loading tiers...</p></div>
            ) : tiers.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon"><i className="fas fa-medal"></i></div>
                    <h3>No Membership Tiers Yet</h3>
                    <p>e.g. Silver (3+ stays), Gold (10+ stays), Platinum (25+ stays), Diamond (50+ stays)</p>
                </div>
            ) : (
                <div className="resource-grid-modern">
                    {tiers.map(tier => (
                        <div key={tier._id} className="resource-card-modern">
                            <div className="card-header">
                                <div className="resource-avatar" style={{ background: `${tier.color}20`, color: tier.color }}>
                                    <i className={`fas ${tier.icon}`}></i>
                                </div>
                                <div className="resource-title">
                                    <h3>{tier.name}</h3>
                                    <span className="resource-subtitle">Level {tier.level}</span>
                                </div>
                                <div className="card-actions">
                                    <button className="action-btn-icon edit" onClick={() => handleEdit(tier)}><i className="fas fa-edit"></i></button>
                                    <button className="action-btn-icon delete" onClick={() => handleDelete(tier)}><i className="fas fa-trash"></i></button>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="status-badges">
                                    <span className={`status-badge ${tier.isActive ? 'active' : 'inactive'}`}><span className="badge-dot"></span>{tier.isActive ? 'Active' : 'Inactive'}</span>
                                    {tier.planId && <span className="status-badge inactive">{tier.planId.name}</span>}
                                </div>
                                <div className="info-grid">
                                    <div className="info-item"><i className="fas fa-bed"></i><div><span className="info-label">Min Stays</span><span className="info-value">{tier.criteria?.minStays || '—'}</span></div></div>
                                    <div className="info-item"><i className="fas fa-rupee-sign"></i><div><span className="info-label">Min Spend</span><span className="info-value">{tier.criteria?.minSpend ? `₹${tier.criteria.minSpend}` : '—'}</span></div></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="modal-overlay-modern" onClick={() => setShowModal(false)}>
                    <div className="modal-modern" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2>{editing ? 'Edit Tier' : 'Add Tier'}</h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body-modern">
                                <div className="form-grid">
                                    <div className="form-group-modern">
                                        <label>Name <span style={{ color: 'var(--danger-color)' }}>*</span></label>
                                        <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Gold" required />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Level (higher = better)</label>
                                        <input type="number" value={formData.level} onChange={e => setFormData({ ...formData, level: Number(e.target.value) })} />
                                    </div>
                                    <div className="form-group-modern full-width">
                                        <label>Plan to Auto-Assign</label>
                                        <select value={formData.planId} onChange={e => setFormData({ ...formData, planId: e.target.value })}>
                                            <option value="">None</option>
                                            {plans.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Min Stays to Qualify</label>
                                        <input type="number" value={formData.criteria.minStays} onChange={e => setFormData({ ...formData, criteria: { ...formData.criteria, minStays: Number(e.target.value) } })} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Min Spend to Qualify (₹)</label>
                                        <input type="number" value={formData.criteria.minSpend} onChange={e => setFormData({ ...formData, criteria: { ...formData.criteria, minSpend: Number(e.target.value) } })} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Color</label>
                                        <input type="color" value={formData.color} onChange={e => setFormData({ ...formData, color: e.target.value })} />
                                    </div>
                                    <div className="form-group-modern full-width">
                                        <label className="checkbox-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <input type="checkbox" checked={formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} />
                                            <span>Active</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-primary-modern"><i className="fas fa-check"></i> {editing ? 'Update' : 'Create'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MembershipTiers;
